import type { AdminCardDto } from '@carcheck/shared';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { initials, shortId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';
import { CardBrandBadge } from '../_components/CardBrandBadge';
import { CardStatusBadge } from '../_components/CardStatusBadge';

/**
 * Column definitions for the cards DataGrid (AUT-126) — the SINGLE source of
 * the card-row renderer, reused verbatim by the standalone `/cards` page AND
 * the inspector-detail Cards tab (no duplication). Extracted from the page
 * so the page stays under the line cap. Sorting is disabled per column — the
 * backend list is fixed `createdAt DESC` (no arbitrary sort).
 *
 * Columns mirror the mockup `cards-list.html`: Инспектор (avatar + holder
 * name + short id) / Карта (`•••• last4` + brand badge — NEVER a full PAN) /
 * Статус / Привязана / action.
 *
 * The mockup's "Последняя выплата" column is DROPPED — `AdminCardDto` has no
 * last-payout field (surfacing it would be cross-context backend work). The
 * mockup's "Привязана" maps to `boundAt`, falling back to `createdAt` when
 * the card never reached `active` (architect guardrail #1).
 *
 * PII discipline: the holder-name cell renders through `<RedactedField
 * perm="view_pii">`, which agrees with `row.piiRevealed` (both derive from
 * the same backend permission). The card cell shows ONLY `•••• {last4}` — the
 * wire type makes a full PAN / token a compile error, so no cell can leak one.
 *
 * Force-unbind action (architect guardrails #2 + #3): the action button is
 * rendered ONLY for `status === 'active'` cards (the backend seam is a
 * guaranteed no-op for any other status — never surface a destructive-looking
 * step-up for a no-op). For every other status the cell is empty. The parent
 * wires `onForceUnbind` (opens the confirm dialog → step-up) + `busy`.
 */

/** Inspector cell: monogram avatar + holder name (PII-gated) + short id. */
const InspectorCell = ({ row, t }: { row: AdminCardDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: 'info.light',
        color: 'info.dark',
      }}
    >
      {initials(row.holderName)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      {row.holderName ? (
        <Typography variant="body2" fontWeight={600} noWrap component="span">
          <RedactedField
            perm="view_pii"
            value={row.holderName}
            redacted={row.holderName}
            {...(row.piiRevealed ? { copyable: true } : {})}
          />
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }} noWrap>
          {t('cards.noValue')}
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
        {shortId(row.inspectorId)}
      </Typography>
    </Box>
  </Box>
);

/** Masked-card cell: `•••• {last4}` (NEVER a full PAN) + brand badge. */
const CardCell = ({ row }: { row: AdminCardDto }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
    <Typography variant="body2" fontWeight={600} fontFamily="monospace" letterSpacing={0.5}>
      {`•••• ${row.last4}`}
    </Typography>
    <CardBrandBadge brand={row.brand} />
  </Box>
);

/** Force-unbind action cell — rendered ONLY for `active` cards. */
const ActionCell = ({
  row,
  t,
  busy,
  onForceUnbind,
}: {
  row: AdminCardDto;
  t: TFunction;
  busy: boolean;
  onForceUnbind: (id: string) => void;
}): ReactElement | null => {
  if (row.status !== 'active') return null;
  return (
    <Button
      size="small"
      color="error"
      variant="text"
      startIcon={<DeleteOutlineIcon />}
      disabled={busy}
      onClick={(e) => {
        e.stopPropagation();
        onForceUnbind(row.id);
      }}
    >
      {t('cards.actions.forceUnbind')}
    </Button>
  );
};

export interface BuildCardColumnsArgs {
  t: TFunction;
  busy: boolean;
  onForceUnbind: (id: string) => void;
}

export const buildCardColumns = ({
  t,
  busy,
  onForceUnbind,
}: BuildCardColumnsArgs): GridColDef<AdminCardDto>[] => [
  {
    field: 'inspector',
    headerName: t('cards.columns.inspector'),
    flex: 1.3,
    minWidth: 220,
    sortable: false,
    renderCell: ({ row }) => <InspectorCell row={row} t={t} />,
  },
  {
    field: 'card',
    headerName: t('cards.columns.card'),
    flex: 1,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => <CardCell row={row} />,
  },
  {
    field: 'status',
    headerName: t('cards.columns.status'),
    flex: 0.6,
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) => <CardStatusBadge status={row.status} />,
  },
  {
    field: 'boundAt',
    headerName: t('cards.columns.boundAt'),
    flex: 0.7,
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {formatDate(row.boundAt ?? row.createdAt)}
      </Typography>
    ),
  },
  {
    field: 'actions',
    headerName: '',
    flex: 0.6,
    minWidth: 140,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => (
      <ActionCell row={row} t={t} busy={busy} onForceUnbind={onForceUnbind} />
    ),
  },
];
